import { policyType, rejectionTypeToPolicyTypeMap } from '@ourbranch/lookups';

import { GET_POLICY_FROM_OFFER_ID } from './offer-queries';

/**
 * Best-effort lookup of the policy bound from an offer (GitHub issue #31622).
 * Returns `{ id, accountId }` when the offer has already been purchased, or null when it
 * hasn't — and also null on any lookup failure, so the warning banner never blocks or
 * breaks the offer page.
 */
export const fetchBoundPolicy = async (client, offerId) => {
  try {
    const { data, error } = await client.query({
      query: GET_POLICY_FROM_OFFER_ID,
      variables: { offerId },
      fetchPolicy: 'no-cache'
    });
    if (error || !data?.getPolicyFromOfferId) {
      return null;
    }
    const { id, accountId } = data.getPolicyFromOfferId;
    return { id, accountId };
  } catch {
    return null;
  }
};

/**
 * Guard for late-arriving bound-policy lookups (#31622): a result may only be applied if
 * it belongs to the most recently REQUESTED offer. Comparing against the loaded offer's id
 * instead would discard the legitimate result whenever the (fast, GSI-backed) lookup wins
 * the race against the heavy GET_OFFER query — i.e. nearly always, and always on first load.
 */
export const shouldApplyBoundPolicyResult = (forOfferId, latestRequestedOfferId) =>
  forOfferId === undefined || forOfferId === latestRequestedOfferId;

// HB/AB are bundle-discounted monoline options for offers linked to an existing customer:
// the shared link carries policyType=HA, but quote-fe only binds the H/A half (it honors
// noBindAuto/noBindHome for existing customers), so rejections gate on that half alone.
const optionToBoundPolicyType = {
  [policyType.HomeBundle]: policyType.Home,
  [policyType.AutoBundle]: policyType.Auto
};

/**
 * Whether the given option is affected by a soft rejection, scoped via
 * rejectionTypeToPolicyTypeMap so a rejection on the opposite policy type doesn't block a
 * clean monoline option (GitHub issue #31905). Rejection types without a map entry don't
 * block, matching the checkout rejectionsSchema and the CannotPurchase notification.
 */
export const hasSoftRejectionForOption = (rejections, selectedOption) => {
  const boundPolicyType = optionToBoundPolicyType[selectedOption] ?? selectedOption;
  return Boolean(
    rejections?.some(
      (rejection) =>
        rejection.level === 'soft' && rejectionTypeToPolicyTypeMap[rejection.type]?.includes(boundPolicyType)
    )
  );
};

export const pollPolicyTable = async (client, offerId, { maxRetries }) => {
  let currentAttempt = 0;
  while (currentAttempt < maxRetries) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    try {
      currentAttempt += 1;
      const { data, error } = await client.query({
        query: GET_POLICY_FROM_OFFER_ID,
        variables: { offerId }
      });
      if (error) {
        throw error;
      }
      const { accountId } = data.getPolicyFromOfferId;
      return { data: { accountId } };
    } catch (error) {
      if (currentAttempt >= maxRetries) {
        throw error;
      }
    }
  }
};
